import React, { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, ShieldAlert, Search, Download, ArrowUpDown, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { MfaEnrollmentWidget } from "@/components/admin/MfaEnrollmentWidget";

const EVENT_LABEL = {
  "auth.login.success": "Login",
  "auth.login.failure": "Login failed",
  "auth.login.blocked": "Login blocked",
  "auth.logout": "Logout",
  "session.invalidated": "Sessions invalidated",
  "password.reset_requested": "Reset requested",
  "password.reset_completed": "Reset completed",
  "password.set": "Password set",
  "verification.email.success": "Email verified",
  "verification.email.failure": "Verify failed",
  "file.private_access.success": "Private file read",
  "file.private_access.denied": "Private file denied",
  "license.redeem.failure": "License redeem failed",
  "webhook.stripe.signature_failure": "Stripe signature failed",
  "webhook.stripe.duplicate": "Stripe duplicate",
  "account.suspended": "Account suspended",
  "account.unsuspended": "Account unsuspended",
  "account.deleted": "Account deleted",
  "account.restored": "Account restored",
  "account.role_changed": "Role changed",
  "account.email_changed": "Email changed",
  "email_change_requested": "Email change requested",
  "email_change_old_email_approved": "Email change approved (old)",
  "email_change_new_email_verified": "Email change verified (new)",
  "email_change_completed": "Email change completed",
  "email_change_cancelled": "Email change cancelled",
  "recovery_email_set": "Recovery email set",
  "recovery_email_verified": "Recovery email verified",
  "recovery_email_removed": "Recovery email removed",
  "email_recovery_requested": "Account recovery requested",
  "email_recovery_new_email_verified": "Recovery: new email verified",
  "email_recovery_recovery_email_confirmed": "Recovery: recovery email confirmed",
  "email_recovery_completed": "Account recovery completed",
  "email_recovery_cancelled": "Account recovery cancelled",
  "email_recovery_failed_or_expired": "Account recovery failed/expired",
  "admin.security_events.viewed": "Security log viewed",
  "admin.security_events.exported": "Security log exported",
  "admin.consent_log.exported": "Consent log exported",
};

const OUTCOME_CLS = {
  success: "bg-teal-50 text-teal-700",
  failure: "bg-red-50 text-red-700",
  blocked: "bg-amber-50 text-amber-700",
  denied: "bg-amber-50 text-amber-700",
  duplicate: "bg-secondary text-muted-foreground",
};
const SEV_DOT = { info: "bg-slate-300", warning: "bg-amber-400", critical: "bg-red-500" };
const PAGE_SIZE = 50;
const DAY = 86400000;

const relTime = (iso) => {
  if (!iso) return "—";
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return "—"; }
};

export default function AdminSecurityEvents() {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(null);

  // Shared filter params for both the table query and the export download.
  const filterParams = useCallback(() => {
    const p = {};
    const now = Date.now();
    if (datePreset === "24h") p.start = new Date(now - DAY).toISOString();
    else if (datePreset === "7d") p.start = new Date(now - 7 * DAY).toISOString();
    else if (datePreset === "30d") p.start = new Date(now - 30 * DAY).toISOString();
    else if (datePreset === "custom") {
      if (customStart) p.start = customStart;
      if (customEnd) p.end = customEnd;
    }
    if (category !== "all") p.category = category;
    if (outcome !== "all") p.outcome = outcome;
    if (sort !== "newest") p.sort = sort;
    if (q) p.q = q;
    return p;
  }, [datePreset, customStart, customEnd, category, outcome, sort, q]);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/admin/security-events", { params: { page, page_size: PAGE_SIZE, ...filterParams() } });
      setData(data);
    } catch {
      setData({ events: [], total: 0 });
    }
  }, [page, filterParams]);
  useEffect(() => { load(); }, [load]);

  const reset = () => setPage(1);
  const applySearch = () => { reset(); setQ(qInput.trim()); };

  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/admin/security-events/export", { params: { format, ...filterParams() }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `security-events.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  if (!data) return <Loading />;
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div data-testid="admin-security-events">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
            <ShieldAlert className="h-5 w-5 text-primary" /> Security events
          </h2>
          <p className="text-xs text-muted-foreground">Read-only auth &amp; security timeline. IP / device data is admin-only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!!exporting || data.total === 0} onClick={() => doExport("csv")} data-testid="security-export-csv">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />{exporting === "csv" ? "Exporting…" : "CSV"}
          </Button>
          <Button size="sm" variant="outline" disabled={!!exporting || data.total === 0} onClick={() => doExport("pdf")} data-testid="security-export-pdf">
            <FileText className="mr-1.5 h-4 w-4" />{exporting === "pdf" ? "Exporting…" : "PDF"}
          </Button>
          <AdminRefreshButton testId="admin-refresh-security" onRefresh={load} />
        </div>
      </div>

      <MfaEnrollmentWidget />

      {/* Filters */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={(v) => { reset(); setCategory(v); }}>
          <SelectTrigger className="w-36" data-testid="security-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(data.categories || []).map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={outcome} onValueChange={(v) => { reset(); setOutcome(v); }}>
          <SelectTrigger className="w-32" data-testid="security-filter-outcome"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            {(data.outcomes || []).map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={(v) => { reset(); setDatePreset(v); }}>
          <SelectTrigger className="w-36" data-testid="security-filter-date"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="24h">Last 24 hours</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => { reset(); setSort(v); }}>
          <SelectTrigger className="w-32" data-testid="security-filter-sort">
            <ArrowUpDown className="mr-1 h-3.5 w-3.5 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder="Search email, IP, target…"
            className="w-52"
            data-testid="security-search-input"
          />
          <Button size="sm" variant="secondary" onClick={applySearch} data-testid="security-search-button"><Search className="h-4 w-4" /></Button>
        </div>
      </div>

      {datePreset === "custom" && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border/60 bg-secondary/40 px-3 py-2" data-testid="security-custom-range">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={customStart} onChange={(e) => { reset(); setCustomStart(e.target.value); }} className="w-40" data-testid="security-date-start" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={customEnd} onChange={(e) => { reset(); setCustomEnd(e.target.value); }} className="w-40" data-testid="security-date-end" />
          </div>
        </div>
      )}

      {data.total === 0 ? (
        <EmptyState icon={ShieldAlert} title="No security events" description="No events match the current filters." testId="admin-security-empty" />
      ) : (
        <>
          <Card className="border-0 p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Target / reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.events.map((e) => (
                    <TableRow key={e.id} data-testid={`security-event-${e.id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground" title={e.created_at}>
                        {relTime(e.created_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT[e.severity] || SEV_DOT.info}`} />
                          <span className="font-[540] text-foreground">{EVENT_LABEL[e.event] || e.event}</span>
                        </span>
                        <div className="font-mono text-[11px] text-muted-foreground">{e.event}</div>
                      </TableCell>
                      <TableCell><Badge className={`${OUTCOME_CLS[e.outcome] || "bg-secondary text-muted-foreground"} capitalize`}>{e.outcome}</Badge></TableCell>
                      <TableCell className="text-sm">
                        <div className="text-foreground">{e.actor_email || (e.actor_user_id ? "—" : <span className="text-muted-foreground">anonymous</span>)}</div>
                        {e.actor_user_id && <div className="font-mono text-[11px] text-muted-foreground">{e.actor_user_id}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground" title={e.user_agent || ""}>{e.ip || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {e.target_type && <span className="capitalize text-foreground">{e.target_type} </span>}
                        {e.target_id && <span className="font-mono text-[11px] text-muted-foreground">{e.target_id}</span>}
                        {e.reason && <div className="text-xs text-muted-foreground">{e.reason}</div>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{data.total} events</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="security-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
              <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="security-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
